import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Card, Empty, Space, Typography } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import PageLoader from '../../../components/Layouts/PageLoader'
import {
  errorNotification,
  getRequest,
  patchRequest,
  paymentGatewaysUrl,
  paymentRouteUrl,
  paymentRoutesUrl,
  successNotification
} from '../../../config'
import GatewayStrip from './GatewayStrip'
import RecentChanges from './RecentChanges'
import ServiceRouteCard from './ServiceRouteCard'

// Which company moves the money, per service and per rail.
//
// A RAIL is how the customer pays — mobile money, card, our merchant number, a bank transfer — and
// has been per-service data since `payment-rails-per-service`. A GATEWAY is the company that moves
// the money on that rail, and until `payment-gateway-routing` there was no such question: Paystack
// was imported directly by every caller. This page is where that second question is answered.
//
// **Every row ships with a blank gateway**, which means "the rail's default", and the defaults
// describe production exactly — so this page changes nothing until somebody edits it.
//
// The server's own sentence is what an operator reads on a refusal. It validates a gateway against
// the rail at the point of saving (`bank → paystack` is not a choice to be discovered when a
// customer taps pay), and its explanation is more use than anything this page could invent.
const PaymentRoutes = () => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [services, setServices] = useState([])
  const [changes, setChanges] = useState([])
  const [gateways, setGateways] = useState([])

  const load = useCallback(async () => {
    const [routes, available] = await Promise.all([
      getRequest(paymentRoutesUrl, 'payment routes', setLoading),
      getRequest(paymentGatewaysUrl, 'payment gateways', () => {})
    ])
    if (routes.success) {
      setServices(routes.data.services || [])
      setChanges(routes.data.changes || [])
    }
    if (available.success) setGateways(available.data.gateways || [])
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // One PATCH per control. The server answers with the row as it now stands, and that answer is
  // what replaces the row here — so a refusal leaves the table showing what the server last said
  // rather than what the operator hoped for.
  const change = async (service, country, row, payload) => {
    const response = await patchRequest(
      paymentRouteUrl(service, country, row.rail),
      payload,
      'the payment route',
      setSaving
    )
    if (!response.success) {
      const said =
        response?.error?.response?.data?.error || response?.error?.response?.data?.detail
      errorNotification(said || 'That route could not be changed.')
      // Re-read rather than guess. The row may have been changed by somebody else in between, and
      // reverting to our own stale copy would hide that.
      await load()
      return
    }
    successNotification('Route updated.')
    // The change log and every row's effective gateway can move together — switching a rail off
    // changes nothing else, but moving a gateway can change what another row falls back to — so
    // the whole page is re-read rather than one row patched in place.
    await load()
  }

  return (
    <PageLoader loading={loading}>
      <div className="container">
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space align="start" style={{ justifyContent: 'space-between', width: '100%' }} wrap>
            <Space direction="vertical" size={0}>
              <Typography.Title level={4} style={{ marginBottom: 0 }}>
                Payment routes
              </Typography.Title>
              <Typography.Text type="secondary">
                Which company takes the money, for each service and each way of paying. A blank
                gateway means that rail&apos;s default.
              </Typography.Text>
            </Space>
            <Button icon={<ReloadOutlined />} onClick={load} disabled={loading || saving}>
              Refresh
            </Button>
          </Space>

          <GatewayStrip gateways={gateways} />

          {services.length ? (
            services.map(service =>
              (service.countries || []).map(country => (
                <ServiceRouteCard
                  key={`${service.key}-${country.country}`}
                  service={service.name || service.key}
                  country={country.country}
                  rails={country.rails}
                  gateways={gateways}
                  saving={saving}
                  onChange={(_name, countryCode, row, payload) =>
                    change(service.key, countryCode, row, payload)
                  }
                />
              ))
            )
          ) : (
            <Empty description="No service takes payments yet." />
          )}

          <Card className="mb-5rem" title="Recent changes">
            <RecentChanges changes={changes} />
          </Card>
        </Space>
      </div>
    </PageLoader>
  )
}

PaymentRoutes.propTypes = {
  history: PropTypes.object
}

export default PaymentRoutes
